import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  ChangeDetectorRef,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { ReceiverTeam } from "../../../models/receiver-team";
import { ApiService } from "../../../services/api.service";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatListOption, MatListModule } from "@angular/material/list";
import { LocationModel } from "../../../models/location.model";
import { StaffModel } from "app/models/staff.model";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { TeamLockAccessComponent } from "../team-lock-access/team-lock-access.component";
import { NgClass } from "@angular/common";
import { CanPipe } from "app/pipes/can.pipe";
import { Areas } from "app/models/permission.model";
import { IRoleSymbols } from "app/models/permission.model";
import { NO_ROLE_SYMBOLS, staffListIconClasses } from "app/shared/role-symbol/role-symbol";
import { StaffSearch, allShownSelected, toggleShownSelection } from "app/shared/role-symbol/staff-search";
import { StaffFilterBarComponent } from "app/shared/role-symbol/staff-filter-bar.component";

@Component({
    selector: "team-edit",
    templateUrl: "./team-edit.component.html",
    styleUrls: ["./team-edit.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CanPipe, FormsModule, ReactiveFormsModule, NgClass, TeamLockAccessComponent, MatSlideToggleModule, MatTooltipModule, MatCheckboxModule, MatListModule, TranslateModule, StaffFilterBarComponent]
})
export class TeamEditComponent implements OnChanges {
  readonly Areas = Areas;

  @Input() team: ReceiverTeam;
  @Input("staff") allStaff: StaffModel[];
  @Input("locations") allLocations: any;
  @Output() closeEditFormEvent = new EventEmitter<void>();
  @Output() teamDeleted = new EventEmitter<string>();
  @Output() teamEdited = new EventEmitter<any>();

  /** Custom roles and who has them, for the staff role symbols and the role filter */
  @Input() roleSymbols: IRoleSymbols = NO_ROLE_SYMBOLS;

  /** Search and role filter of the staff list, shared with the alarm route recipient picker (ES-7564) */
  readonly search = new StaffSearch(() => this.roleSymbols, () => this.currentUserLevel(), this.translate);

  constructor(
    private api: ApiService,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef
  ) {}

  user: any;
  teamEditForm: UntypedFormGroup;
  editTeamFormError: string;
  deleteConfirmation: boolean;
  availableStaffForCompany: StaffModel[];
  availableStaffForLocation: StaffModel[];
  activeTab: number = 1;

  createTeamEditForm(): void {
    this.teamEditForm = this.fb.group({
      groupName: [
        this.team.groupName,
        {
          validators: [Validators.required],
        },
      ],
      selectedTeamMembers: this.team.users
        ? new UntypedFormControl(
            this.team.users.map((x) => {
              return x.id;
            })
          )
        : "",
      teamLocked: this.team.locked,
      teamHidden: this.team.hidden,
    });
    //Run change detection after selection of staffs were changed
    this.cdRef.detectChanges();
  }

  editTeam(): void {
    const groupData = {
      groupId: this.team.groupId,
      groupName: this.teamEditForm.value.groupName,
      locked: this.teamEditForm.value.teamLocked,
      hidden: this.teamEditForm.value.teamHidden,
      staffs: this.teamEditForm.value.selectedTeamMembers,
    };

    console.log(groupData);

    this.api.editReceiverTeam(groupData).subscribe(
      (res) => {
        groupData.staffs = res;
        this.teamEdited.emit(groupData);
        this.closeEditForm();
      },
      (err) => {
        const backendMessage = err?.error?.errors?.[0];
        this.editTeamFormError = backendMessage
          ? this.translate.instant(backendMessage)
          : this.translate.instant('ERROR');
      }
    );
  }

  featureEnabled(feature: string): boolean {
    const features: [string] = JSON.parse(localStorage.getItem("features"));
    if (features) {
      return features.includes(feature);
    }
    return false;
  }

  deleteTeam(): void {
    this.api.deleteReceiverTeam(this.team.groupId).subscribe(
      (res) => {
        this.teamDeleted.emit(this.team.groupId);
        this.closeEditForm();
      },
      (err) => {
        this.translate.get(["ERROR"]).subscribe((t) => {
          this.editTeamFormError = t.ERROR;
        });
      }
    );
  }

  closeEditForm(): void {
    this.closeEditFormEvent.emit();
  }

  parseStaffList(staff: StaffModel[]): void {
    this.availableStaffForCompany = [];
    this.availableStaffForLocation = [];
    if (this.team.locationId) {
      this.availableStaffForLocation = (staff ?? []).filter(
        (staff) => staff.locationId === this.team.locationId
      );
    }
    this.availableStaffForCompany = (staff ?? []).filter((staff) =>
      staff.locationId.startsWith("C0")
    );
  }

  ngOnChanges(changes?: SimpleChanges) {
    // New role symbols only change the icons and the role filter: keep the form as the user has it
    if (changes && Object.keys(changes).every(key => key === 'roleSymbols')) return;
    this.user = JSON.parse(localStorage.getItem("user"));
    this.parseStaffList(this.allStaff);
    this.createTeamEditForm();
  }

  private currentUserLevel(): number | undefined {
    try {
      return JSON.parse(localStorage.getItem("user"))?.roleLevel;
    } catch {
      return undefined;
    }
  }

  /** Staff icon: role symbol, shield for a login identity (same as Staff Management) */
  staffIcon(staff: StaffModel): string {
    return `${staffListIconClasses(staff, this.roleSymbols)} fa-fw`;
  }

  /**
   * Is the staff member shown with the search and role filter. Hidden ones stay in the list (hidden
   * with CSS): removing an option from the selection list would unselect it, and so drop a member.
   */
  isShown(staff: StaffModel): boolean {
    return !this.search.active || this.search.matches({
      id: staff.id, name: staff.fullName, gsmNumber: staff.gsmNumber, level: staff.usingLevel
    });
  }

  shownStaff(staff: StaffModel[] | null | undefined): StaffModel[] {
    return (staff ?? []).filter(s => this.isShown(s));
  }

  private shownStaffIds(): string[] {
    return [...this.shownStaff(this.availableStaffForCompany), ...this.shownStaff(this.availableStaffForLocation)].map(s => s.id);
  }

  allShownSelected(): boolean {
    return allShownSelected(this.teamEditForm.controls.selectedTeamMembers.value, this.shownStaffIds());
  }

  /** Select all: selects the staff the search shows, or unselects them when all are selected */
  toggleAllSelection(): void {
    const control = this.teamEditForm.controls.selectedTeamMembers;
    control.patchValue(toggleShownSelection(control.value, this.shownStaffIds()));
    control.markAsDirty();
  }

}
